/**
 * The live-update client (1.1 deliverable 22). One `EventSource` on `/api/events` per tab, through
 * contracts' `subscribeAppEvents`, opened at the first subscriber and closed after the last,
 * however many parts of the page subscribe. The mock-mode build (`VITE_CODEX_MODE=mock`, P0-3's
 * build-time flag, folded away by the production build) opens nothing. A malformed or unknown
 * frame is counted, never thrown; a listener that throws does not stop the others (its error is
 * rethrown on its own, so the page still reports it). `subscribeOpen` hears each opening of the
 * stream (at once when it is open, and after every reconnect): a change made before the stream
 * was up, or while it was down, is announced to nobody, so a subscriber reads what it follows
 * again then (DECISIONS 463).
 */
import {
  APP_EVENT_TYPES,
  type AppEventHandlers,
  type AppEventSubscription,
  type AppEventType,
  type AppEventV1,
  type DocChangedData,
  subscribeAppEvents,
} from '@codex/contracts';

/** The stream's path (`events.stream`). */
export const APP_EVENTS_URL = '/api/events';

export type AppEventListener<T extends AppEventType> = (
  event: Extract<AppEventV1, { type: T }>,
) => void;

export interface AppEventStats {
  /** Frames that could not be decoded. */
  readonly malformed: number;
  /** Events of a type (or version) this build does not know. */
  readonly unknown: number;
  /** Whether the stream is open now. */
  readonly open: boolean;
}

export interface AppEvents {
  /** Every event of one type; returns the unsubscribe. */
  subscribe<T extends AppEventType>(type: T, listener: AppEventListener<T>): () => void;
  /** Every `doc.changed` of one document; returns the unsubscribe. */
  subscribeDocChanged(docId: string, listener: (data: DocChangedData) => void): () => void;
  /**
   * Called each time the stream is open: in a microtask when it already is, then after every
   * reconnect. It never opens the stream itself; returns the unsubscribe.
   */
  subscribeOpen(listener: () => void): () => void;
  stats(): AppEventStats;
}

export interface AppEventsOptions {
  readonly url?: string;
  /** False opens nothing. Default: true unless the build is the mock-mode build. */
  readonly enabled?: () => boolean;
  /** For specs; contracts' `subscribeAppEvents` by default. */
  readonly subscribe?: typeof subscribeAppEvents;
}

/** True only in the mock-mode build; the production build folds the comparison away. */
export function isMockModeBuild(): boolean {
  return import.meta.env.VITE_CODEX_MODE === 'mock';
}

export function createAppEvents(options: AppEventsOptions = {}): AppEvents {
  const url = options.url ?? APP_EVENTS_URL;
  const enabled = options.enabled ?? (() => !isMockModeBuild());
  const subscribe = options.subscribe ?? subscribeAppEvents;
  const listeners = new Map<AppEventType, Set<(event: AppEventV1) => void>>();
  let count = 0;
  let stream: AppEventSubscription | null = null;
  let malformed = 0;
  let unknownBefore = 0;
  /** Whether the stream is connected now (between its `open` and its next error or close). */
  let connected = false;
  const openListeners = new Set<() => void>();

  const call = (listener: () => void) => {
    try {
      listener();
    } catch (error) {
      queueMicrotask(() => {
        throw error;
      });
    }
  };

  const dispatch = (event: AppEventV1) => {
    for (const listener of [...(listeners.get(event.type) ?? [])]) {
      call(() => listener(event));
    }
  };

  const open = () => {
    if (stream !== null || !enabled()) return;
    const handlers: Record<string, unknown> = {
      onMalformed: () => {
        malformed += 1;
      },
      onOpen: () => {
        connected = true;
        for (const listener of [...openListeners]) call(listener);
      },
      onError: () => {
        // `EventSource` reconnects by itself; its next `open` calls the open listeners again.
        connected = false;
      },
    };
    for (const type of APP_EVENT_TYPES) handlers[type] = dispatch;
    stream = subscribe(url, handlers as AppEventHandlers, { enabled: true });
  };

  const close = () => {
    if (stream === null) return;
    connected = false;
    unknownBefore += stream.stats.unknown;
    stream();
    stream = null;
  };

  const add = (type: AppEventType, listener: (event: AppEventV1) => void) => {
    let set = listeners.get(type);
    if (set === undefined) {
      set = new Set();
      listeners.set(type, set);
    }
    // A wrapper of its own, so the same function subscribed twice is two subscriptions.
    const entry = (event: AppEventV1) => listener(event);
    set.add(entry);
    count += 1;
    open();
    let active = true;
    return () => {
      if (!active) return;
      active = false;
      set.delete(entry);
      count -= 1;
      if (count === 0) close();
    };
  };

  return {
    subscribe: (type, listener) => add(type, listener as (event: AppEventV1) => void),
    subscribeDocChanged: (docId, listener) =>
      add('doc.changed', (event) => {
        if (event.type === 'doc.changed' && event.data.docId === docId) listener(event.data);
      }),
    subscribeOpen: (listener) => {
      // A wrapper of its own, as `add` keeps one.
      const entry = () => listener();
      openListeners.add(entry);
      if (connected) {
        queueMicrotask(() => {
          if (connected && openListeners.has(entry)) call(entry);
        });
      }
      return () => {
        openListeners.delete(entry);
      };
    },
    stats: () => ({
      malformed,
      unknown: unknownBefore + (stream?.stats.unknown ?? 0),
      open: stream !== null,
    }),
  };
}

/** The tab's one live-update client. */
export const appEvents: AppEvents = createAppEvents();
